/**
 * ErrorBoundary Component
 * 
 * Catches React component errors and displays a user-friendly error message
 * with crash report download functionality.
 * 
 * Integrated with centralized error tracking module.
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { AlertTriangle, Download, Mail } from 'lucide-react';
import { captureException } from '@/app/utils/errorTracking';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  crashReport: string | null;
}

interface CrashReport {
  metadata: {
    timestamp: string;
    appName: string;
    version: string;
    reportId: string;
  };
  error: {
    name: string;
    message: string;
    stack: string | undefined;
  };
  componentStack: string | undefined;
  browserInfo: {
    userAgent: string;
    language: string;
    platform: string;
    screenResolution: string;
    viewport: string;
    cookiesEnabled: boolean;
    onlineStatus: boolean;
  };
  appState: {
    url: string;
  };
  performanceMetrics: {
    memoryUsage?: {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
    timing?: {
      loadTime: number;
      domContentLoaded: number;
    };
  };
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    crashReport: null,
  };

  private generateReportId(): string {
    return `crash-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  private generateCrashReport(error: Error, errorInfo: ErrorInfo): string {
    const reportId = this.generateReportId();
    
    const report: CrashReport = {
      metadata: {
        timestamp: new Date().toISOString(),
        appName: 'Isometrische 2D-CAD Webapp',
        version: '1.0.0',
        reportId: reportId,
      },
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      componentStack: errorInfo.componentStack,
      browserInfo: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        cookiesEnabled: navigator.cookieEnabled,
        onlineStatus: navigator.onLine,
      },
      appState: {
        url: window.location.href,
      },
      performanceMetrics: {
        memoryUsage: (performance as any).memory ? {
          usedJSHeapSize: (performance as any).memory.usedJSHeapSize,
          totalJSHeapSize: (performance as any).memory.totalJSHeapSize,
          jsHeapSizeLimit: (performance as any).memory.jsHeapSizeLimit,
        } : undefined,
        timing: performance.timing ? {
          loadTime: performance.timing.loadEventEnd - performance.timing.navigationStart,
          domContentLoaded: performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart,
        } : undefined,
      },
    };

    // Format as readable text with clear sections
    const reportText = `
╔═══════════════════════════════════════════════════════════════╗
║                    CRASH REPORT                                ║
║          Isometrische 2D-CAD Webapp - Fehlerprotokoll         ║
╚═══════════════════════════════════════════════════════════════╝

📋 METADATEN
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Report ID:    ${report.metadata.reportId}
Zeitstempel:  ${report.metadata.timestamp}
App Name:     ${report.metadata.appName}
Version:      ${report.metadata.version}

❌ FEHLERINFORMATIONEN
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Fehlertyp:    ${report.error.name}
Nachricht:    ${report.error.message}

Stack Trace:
${report.error.stack || 'Kein Stack Trace verfügbar'}

Component Stack:
${report.componentStack || 'Kein Component Stack verfügbar'}

🌐 BROWSER-INFORMATIONEN
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
User Agent:        ${report.browserInfo.userAgent}
Sprache:           ${report.browserInfo.language}
Platform:          ${report.browserInfo.platform}
Bildschirmauflösung: ${report.browserInfo.screenResolution}
Viewport-Größe:    ${report.browserInfo.viewport}
Cookies aktiviert: ${report.browserInfo.cookiesEnabled}
Online-Status:     ${report.browserInfo.onlineStatus}

💾 APP-STATUS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
URL: ${report.appState.url}

⚡ PERFORMANCE-METRIKEN
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${report.performanceMetrics.memoryUsage ? `
Speichernutzung:
  - Verwendet: ${(report.performanceMetrics.memoryUsage.usedJSHeapSize / 1024 / 1024).toFixed(2)} MB
  - Total:     ${(report.performanceMetrics.memoryUsage.totalJSHeapSize / 1024 / 1024).toFixed(2)} MB
  - Limit:     ${(report.performanceMetrics.memoryUsage.jsHeapSizeLimit / 1024 / 1024).toFixed(2)} MB
` : 'Keine Speicherinformationen verfügbar'}

${report.performanceMetrics.timing ? `
Timing:
  - Load Time:         ${report.performanceMetrics.timing.loadTime}ms
  - DOM Content Loaded: ${report.performanceMetrics.timing.domContentLoaded}ms
` : 'Keine Timing-Informationen verfügbar'}

╔═══════════════════════════════════════════════════════════════╗
║ ANWEISUNGEN FÜR DEN ENTWICKLER                                ║
╚═══════════════════════════════════════════════════════════════╝

1. Fehlertyp und Message analysieren
2. Stack Trace auf betroffene Dateien/Zeilen prüfen
3. Component Stack zeigt React-Komponenten-Hierarchie
4. Browser-Info hilft bei browserspezifischen Problemen

Bitte senden Sie diesen Report an: support@example.com
oder erstellen Sie ein GitHub Issue mit diesem Report.

═══════════════════════════════════════════════════════════════
Ende des Reports
═══════════════════════════════════════════════════════════════
`;

    return reportText;
  }

  private downloadCrashReport = () => {
    if (!this.state.crashReport) return;

    const reportId = this.generateReportId();
    const filename = `crash-report-${reportId}.txt`;
    
    const blob = new Blob([this.state.crashReport], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  private sendEmailWithReport = () => {
    if (!this.state.crashReport || !this.state.error) return;

    const subject = encodeURIComponent(
      `Crash Report: ${this.state.error.name} - ${new Date().toISOString()}`
    );
    const body = encodeURIComponent(
      `Hallo Support-Team,\n\ndie App ist abgestürzt. Anbei der Crash Report:\n\n` +
      `[Bitte fügen Sie den heruntergeladenen Crash-Report als Anhang hinzu]\n\n` +
      `Fehler: ${this.state.error.message}\n\n` +
      `Kurzbeschreibung des Problems:\n` +
      `[Bitte beschreiben Sie, was Sie getan haben, bevor der Fehler auftrat]\n\n` +
      `Mit freundlichen Grüßen`
    );

    // Open email client
    window.location.href = `mailto:support@example.com?subject=${subject}&body=${body}`;
  };

  private reloadApp = () => {
    window.location.reload();
  };

  public static getDerivedStateFromError(error: Error): State {
    return { 
      hasError: true, 
      error,
      errorInfo: null,
      crashReport: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Send error to centralized error tracking
    captureException(error, {
      tags: {
        errorBoundary: 'true',
        componentName: 'ErrorBoundary',
      },
      extra: {
        componentStack: errorInfo.componentStack,
      },
      level: 'fatal',
    });
    
    // Generate crash report
    const crashReport = this.generateCrashReport(error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
      crashReport,
    });

    // Automatically download crash report after a short delay
    setTimeout(() => {
      const blob = new Blob([crashReport], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crash-report-${this.generateReportId()}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    }, 500);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <AlertTriangle className="w-16 h-16 text-red-500" />
              </div>
              <CardTitle className="text-2xl text-red-600">
                Anwendungsfehler
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-800 mb-2">
                  Fehlermeldung:
                </h3>
                <p className="text-red-700 font-mono text-sm">
                  {this.state.error?.message}
                </p>
              </div>

              <div className="space-y-4">
                <p className="text-gray-700">
                  Es ist ein unerwarteter Fehler aufgetreten. Ein detaillierter Crash-Report 
                  wurde automatisch heruntergeladen.
                </p>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">
                    📧 Hilfe anfordern:
                  </h4>
                  <p className="text-sm text-blue-700 mb-3">
                    Bitte senden Sie den heruntergeladenen Crash-Report per E-Mail an das Support-Team, 
                    damit wir das Problem schnell beheben können.
                  </p>
                  <Button
                    onClick={this.sendEmailWithReport}
                    variant="outline"
                    className="w-full"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    E-Mail mit Report senden
                  </Button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button
                    onClick={this.downloadCrashReport}
                    variant="outline"
                    className="w-full"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Report erneut herunterladen
                  </Button>
                  <Button
                    onClick={this.reloadApp}
                    variant="default"
                    className="w-full"
                  >
                    Anwendung neu laden
                  </Button>
                </div>
              </div>

              {process.env.NODE_ENV === 'development' && this.state.error?.stack && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                    Stack Trace anzeigen (nur Development)
                  </summary>
                  <pre className="mt-2 p-3 bg-gray-900 text-green-400 text-xs overflow-auto rounded max-h-64">
                    {this.state.error.stack}
                  </pre>
                </details>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// Global error handlers for non-React errors
// These will also be captured by Sentry if enabled
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    console.error('Global error caught:', event.error);
    
    // Send to error tracking
    captureException(event.error || new Error(event.message), {
      tags: {
        errorType: 'global',
      },
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    
    // Send to error tracking
    captureException(
      event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
      {
        tags: {
          errorType: 'unhandledRejection',
        },
      }
    );
  });
}
